import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { CellColorPicker } from '@/components/ui/CellColorPicker';
import { CellDateColorPicker } from '@/components/ui/CellDateColorPicker';
import { CellSelectColorPicker } from '@/components/ui/CellSelectColorPicker';
import { CellChassisColorPicker } from '@/components/ui/CellChassisColorPicker';
import { ColoredText } from '@/components/ui/ColoredText';
import { 
  Star, Truck, FileSignature, MapPin
} from 'lucide-react';

/**
 * ExportTableCell - Renders individual cells for the exports table
 * Extracted from ExportsTable.js to reduce complexity
 */

// Helper: Render colored cell with color picker
const ColoredCell = ({ exp, columnId, value, customColor, onCellColorChange }) => (
  <CellColorPicker
    currentColor={exp.cell_colors?.[columnId]}
    onColorChange={(color) => onCellColorChange(exp.id, columnId, color)}
  >
    {customColor ? (
      <span style={{ color: exp.cell_colors?.[columnId] || customColor }}>
        {value}
      </span>
    ) : (
      <ColoredText color={exp.cell_colors?.[columnId]}>
        {value}
      </ColoredText>
    )}
  </CellColorPicker>
);

// Important Star Cell
export const ImportantStarCell = ({ exp, onToggleImportant }) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={() => onToggleImportant(exp.id, exp.is_important)}
    className="h-6 w-6 p-0"
  >
    <Star 
      className={`h-4 w-4 ${exp.is_important ? 'fill-yellow-400 text-yellow-400' : 'text-slate-600'}`} 
    />
  </Button>
);

// Hex color map for status markers (inline styles need actual color values)
const STATUS_MARKER_COLORS = {
  'Empty in Customer': '#FFDAB9',
  'Available for Pickup': '#90EE90',
  'Need Appt': '#1a1a1a',
  'On the way to Customer': '#FFB6C1',
  'Important Customer': '#A0522D',
  'Delivered to Customer': '#FFC0CB',
  'In Yard': '#DDA0DD',
  'Container Returned': '#D3D3D3',
  'On the way to Return': '#AFEEEE',
  'Delivered to Exam Location': '#8B4513',
  'New Shipment': '#E6E6FA',
  'Empty Container in Yard': '#FFFFE0',
  'On the way to Pickup': '#228B22',
  'Shipment Closed': '#ADD8E6',
  'Trouble': '#FA8072'
};

// Status Marker Cell - shows green blink for last edited, status color otherwise
export const StatusMarkerCell = ({ status, isLastEdited }) => {
  if (isLastEdited) {
    return (
      <div 
        className="w-2 h-full min-h-[40px] bg-green-500 animate-blink rounded-sm" 
        title="Last edited"
      />
    );
  }
  return (
    <div 
      className="w-2 h-full min-h-[40px]" 
      style={{ backgroundColor: STATUS_MARKER_COLORS[status] || '#666' }}
    />
  );
};

// Actions Cell - matches Imports style (no dropdown, no delete)
export const ActionsCell = ({ exp, onOpenDispatch, onEditExport, onOpenStatusChange }) => (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
      onClick={() => onOpenDispatch(exp)}
      title="Send Dispatch"
      data-testid={`dispatch-btn-${exp.id}`}
    >
      <Truck className="h-3.5 w-3.5" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
      onClick={() => onEditExport(exp)}
      title="Edit Export"
      data-testid={`edit-btn-${exp.id}`}
    >
      <FileSignature className="h-3.5 w-3.5" />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-800"
      onClick={() => onOpenStatusChange(exp)}
      title="Change Location / Status"
      data-testid={`change-location-btn-${exp.id}`}
    >
      <MapPin className="h-3.5 w-3.5" />
    </Button>
  </div>
);

// Status Dropdown Cell
export const StatusDropdownCell = ({ exp, statusColors, statuses, onStatusChange }) => (
  <Select
    value={exp.status || 'New Shipment'}
    onValueChange={(val) => onStatusChange(exp.id, val)}
  >
    <SelectTrigger className="h-7 text-xs bg-transparent border-0 w-[110px]">
      <Badge className={`${statusColors[exp.status] || 'bg-slate-600'} text-white text-[10px]`}>
        {exp.status || 'New Shipment'}
      </Badge>
    </SelectTrigger>
    <SelectContent className="bg-slate-900 border-slate-700">
      {statuses.map(status => (
        <SelectItem key={status} value={status}>
          <Badge className={`${statusColors[status]} text-white text-xs`}>
            {status}
          </Badge>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Overweight Badge Cell
export const OverweightCell = ({ isOverweight }) => (
  isOverweight ? (
    <Badge className="bg-red-500/20 text-red-400 border border-red-500/50 text-[10px] px-1.5 py-0.5 font-bold" title="Overweight Container">
      OW
    </Badge>
  ) : (
    <span className="text-slate-600">-</span>
  )
);

// Hazmat Badge Cell
export const HazmatCell = ({ isHazmat }) => (
  isHazmat ? (
    <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/50 text-[10px] px-1.5 py-0.5 font-bold" title="Hazmat Container" data-testid="hazmat-badge">
      HZ
    </Badge>
  ) : (
    <span className="text-slate-600">-</span>
  )
);

/**
 * Main ExportTableCell Component
 * Renders the appropriate cell content based on column ID
 */
export const ExportTableCell = ({
  columnId,
  exp,
  // Actions handlers
  onToggleImportant,
  onOpenDispatch,
  onEditExport,
  onOpenStatusChange,
  onStatusChange,
  onCellColorChange,
  // Inline edit handlers
  onInlineDateSave,
  onChassisSave,
  onPPCTFSave,
  // Data
  statusColors,
  statuses,
  lastEditedExportId,
  masterData = {},
  // Helpers
  formatDate,
  formatTime,
  getCellTextColor,
  // Inline text edit (e.g. remarks)
  canEdit = true,
  inlineEditCell,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  handleInlineEditSave,
  cancelInlineEdit
}) => {
  switch (columnId) {
    case 'marker':
      return <StatusMarkerCell status={exp.status} isLastEdited={lastEditedExportId === exp.id} />;
    
    case 'important':
      return <ImportantStarCell exp={exp} onToggleImportant={onToggleImportant} />;
    
    case 'actions':
      return (
        <ActionsCell 
          exp={exp}
          onOpenDispatch={onOpenDispatch}
          onEditExport={onEditExport}
          onOpenStatusChange={onOpenStatusChange}
        />
      );
    
    case 'booking_number':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="booking_number" 
          value={exp.booking_number || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'mbl_number':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="mbl_number" 
          value={exp.mbl_number || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'container_number':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="container_number" 
          value={exp.container_number || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'container_size':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="container_size" 
          value={exp.container_size || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'chassis':
      return (
        <CellChassisColorPicker
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.chassis}
          currentChassisType={exp.chassis_type}
          currentChassisNumber={exp.chassis_number}
          onColorChange={(color) => onCellColorChange(exp.id, 'chassis', color)}
          onSave={(chassisType, chassisNumber) => onChassisSave && onChassisSave(exp.id, chassisType, chassisNumber)}
          fieldLabel="Chassis"
        >
          <ColoredText 
            color={exp.cell_colors?.chassis}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded whitespace-normal break-all"
            title="Click to edit chassis"
          >
            {exp.chassis_type && exp.chassis_number 
              ? `${exp.chassis_type}/${exp.chassis_number}` 
              : exp.chassis_number || exp.chassis_type || '-'}
          </ColoredText>
        </CellChassisColorPicker>
      );
    
    case 'overweight':
      return <OverweightCell isOverweight={exp.is_overweight} />;
    
    case 'hazmat':
      return <HazmatCell isHazmat={exp.is_hazmat} />;
    
    case 'ssl':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="ssl" 
          value={exp.ssl || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'pp_ctf': {
      const ppPaid = exp.pier_pass_paid;
      const ctfPaid = exp.ctf_paid;
      return (
        <CellColorPicker
          currentColor={exp.cell_colors?.pp_ctf}
          onColorChange={(color) => onCellColorChange(exp.id, 'pp_ctf', color)}
        >
          <div 
            className="flex items-center justify-center gap-1 cursor-pointer hover:bg-slate-700/50 px-2 py-1 rounded min-w-[40px]"
            title={`PP: ${ppPaid ? 'Paid' : 'Unpaid'} | CTF: ${ctfPaid ? 'Paid' : 'Unpaid'}${exp.pier_pass_paid_by ? '\nPP by: ' + exp.pier_pass_paid_by : ''}${exp.ctf_paid_by ? '\nCTF by: ' + exp.ctf_paid_by : ''}\nClick to toggle PP`}
            onClick={() => onPPCTFSave && onPPCTFSave(exp.id, { 
              pier_pass_paid: !ppPaid, 
              ctf_paid: ctfPaid,
              pier_pass_paid_by: exp.pier_pass_paid_by,
              ctf_paid_by: exp.ctf_paid_by
            })}
          >
            {ppPaid && (
              <span className="w-2 h-2 bg-red-500 rounded-full" title={`PP Paid${exp.pier_pass_paid_by ? ' by ' + exp.pier_pass_paid_by : ''}`} />
            )}
            {ctfPaid && (
              <span className="w-2 h-2 bg-red-500 rounded-full" title={`CTF Paid${exp.ctf_paid_by ? ' by ' + exp.ctf_paid_by : ''}`} />
            )}
            {!ppPaid && !ctfPaid && <span className="text-slate-500 text-xs">-</span>}
          </div>
        </CellColorPicker>
      );
    }
    
    case 'vessel_name':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="vessel_name" 
          value={exp.vessel_name || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'customer_name':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="customer_name" 
          value={exp.customer_name || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'destination_port':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="destination_port" 
          value={exp.destination_port || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'cutoff_date':
      return (
        <CellDateColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.cutoff_date}
          currentDate={exp.cutoff_date}
          currentTimeFrom={exp.cutoff_time}
          onColorChange={(color) => onCellColorChange(exp.id, 'cutoff_date', color)}
          onDateChange={(date) => onInlineDateSave && onInlineDateSave(exp.id, 'cutoff_date', date)}
          onTimeChange={(time) => onInlineDateSave && onInlineDateSave(exp.id, 'cutoff_time', time)}
          fieldLabel="Cutoff Date & Time"
          showTimePicker={true}
        >
          <ColoredText 
            color={exp.cell_colors?.cutoff_date || (exp.cutoff_date ? '#dc2626' : undefined)}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded min-w-[60px] inline-block"
            title="Click to edit cutoff"
          >
            {formatDate(exp.cutoff_date) || '-'}
            {exp.cutoff_time && (
              <ColoredText color={exp.cell_colors?.cutoff_date} className="block text-[10px]">
                {formatTime ? formatTime(exp.cutoff_time) : exp.cutoff_time}
              </ColoredText>
            )}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'erd':
      return (
        <CellDateColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.erd}
          currentDate={exp.erd}
          onColorChange={(color) => onCellColorChange(exp.id, 'erd', color)}
          onDateChange={(date) => onInlineDateSave && onInlineDateSave(exp.id, 'erd', date)}
          fieldLabel="Early Return Date"
        >
          <ColoredText 
            color={exp.cell_colors?.erd || (exp.erd ? '#d97706' : undefined)}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded min-w-[50px] inline-block"
            title="Click to edit ERD"
          >
            {formatDate(exp.erd) || '-'}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'load_rdy':
      return (
        <CellDateColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.load_rdy}
          currentDate={exp.load_rdy}
          currentTimeFrom={exp.load_rdy_time}
          onColorChange={(color) => onCellColorChange(exp.id, 'load_rdy', color)}
          onDateChange={(date) => onInlineDateSave && onInlineDateSave(exp.id, 'load_rdy', date)}
          onTimeChange={(time) => onInlineDateSave && onInlineDateSave(exp.id, 'load_rdy_time', time)}
          fieldLabel="Load Ready Date & Time"
          showTimePicker={true}
        >
          <ColoredText 
            color={exp.cell_colors?.load_rdy || (exp.load_rdy ? '#16a34a' : undefined)}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded min-w-[60px] inline-block"
            title="Click to edit load ready"
          >
            {formatDate(exp.load_rdy) || '-'}
            {exp.load_rdy_time && (
              <ColoredText color={exp.cell_colors?.load_rdy} className="block text-[10px]">
                {formatTime ? formatTime(exp.load_rdy_time) : exp.load_rdy_time}
              </ColoredText>
            )}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'load_return':
      return (
        <CellDateColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.load_return}
          currentDate={exp.load_return}
          onColorChange={(color) => onCellColorChange(exp.id, 'load_return', color)}
          onDateChange={(date) => onInlineDateSave && onInlineDateSave(exp.id, 'load_return', date)}
          fieldLabel="Load Return Date"
        >
          <ColoredText 
            color={exp.cell_colors?.load_return || (exp.load_return ? '#2563eb' : undefined)}
            className="text-xs cursor-pointer hover:bg-slate-700/50 px-1 rounded min-w-[50px] inline-block"
            title="Click to edit return date"
          >
            {formatDate(exp.load_return) || '-'}
          </ColoredText>
        </CellDateColorPicker>
      );
    
    case 'status':
      return (
        <StatusDropdownCell 
          exp={exp}
          statusColors={statusColors}
          statuses={statuses}
          onStatusChange={onStatusChange}
        />
      );
    
    case 'driver_name':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="driver_name" 
          value={exp.driver_name || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'pickup_address':
      return (
        <ColoredCell 
          exp={exp} 
          columnId="pickup_address" 
          value={exp.pickup_address || '-'} 
          onCellColorChange={onCellColorChange}
        />
      );
    
    case 'remarks':
      // Inline-editable like the imports dashboard. When inlineEditCell matches
      // this export+field, render a textarea; otherwise render the static cell
      // wrapped in a click-to-edit handler.
      if (inlineEditCell?.rowId === exp.id && inlineEditCell?.field === 'remarks') {
        return (
          <textarea
            value={inlineEditValue}
            onChange={(e) => setInlineEditValue(e.target.value)}
            onBlur={handleInlineEditSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleInlineEditSave();
              }
              if (e.key === 'Escape') cancelInlineEdit();
            }}
            autoFocus
            rows={2}
            className="px-1 py-0.5 text-xs bg-slate-800 border border-blue-500 text-white w-full min-w-[120px] rounded resize-none"
            onClick={(e) => e.stopPropagation()}
            placeholder="Enter remarks..."
            data-testid={`export-remarks-textarea-${exp.id}`}
          />
        );
      }
      return (
        <CellColorPicker
          currentColor={exp.cell_colors?.remarks}
          onColorChange={(color) => onCellColorChange(exp.id, 'remarks', color)}
        >
          <div
            className="w-full h-full min-h-[28px] cursor-pointer hover:bg-slate-700/50 rounded flex items-center"
            onClick={(e) => {
              e.stopPropagation();
              if (canEdit && startInlineEdit) startInlineEdit(exp.id, 'remarks', exp.remarks);
            }}
            title={exp.remarks || (canEdit ? 'Click to edit, Right-click for color' : '')}
            data-testid={`export-remarks-cell-${exp.id}`}
          >
            <ColoredText color={exp.cell_colors?.remarks}>
              <span
                className="text-xs px-1 block w-full"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: '1.3',
                  maxHeight: '2.6em'
                }}
              >
                {exp.remarks || '-'}
              </span>
            </ColoredText>
          </div>
        </CellColorPicker>
      );
    
    case 'terminal':
      return (
        <CellSelectColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.terminal}
          currentValue={exp.pickup_terminal_id || exp.terminal_id}
          options={masterData.terminals || []}
          onColorChange={(color) => onCellColorChange(exp.id, 'terminal', color)}
          onValueChange={(value) => {
            if (onInlineDateSave) {
              onInlineDateSave(exp.id, 'pickup_terminal_id', value);
            }
          }}
          fieldLabel="Terminal"
          placeholder="Select terminal..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={exp.cell_colors?.terminal}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
            title={exp.pickup_terminal_name || exp.terminal_name || 'Click to select terminal'}
          >
            {exp.pickup_terminal_name || exp.terminal_name || '-'}
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'return_terminal':
      return (
        <CellSelectColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.return_terminal}
          currentValue={exp.return_terminal_id || exp.return_terminal}
          options={masterData.terminals || []}
          onColorChange={(color) => onCellColorChange(exp.id, 'return_terminal', color)}
          onValueChange={(value) => {
            const terminal = (masterData.terminals || []).find(t => t.id === value);
            if (onInlineDateSave) {
              onInlineDateSave(exp.id, 'return_terminal_id', value);
            }
          }}
          fieldLabel="Return Terminal"
          placeholder="Select terminal..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={exp.cell_colors?.return_terminal}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[40px] inline-block"
            title={exp.return_terminal_name || 'Click to select return terminal'}
          >
            {exp.return_terminal_name || '-'}
          </ColoredText>
        </CellSelectColorPicker>
      );
    
    case 'yard': {
      const currentYard = (masterData.yards || []).find(y => y.id === exp.yard_id);
      return (
        <CellSelectColorPicker 
          containerNumber={exp.container_number}
          currentColor={exp.cell_colors?.yard}
          currentValue={exp.yard_id}
          options={masterData.yards || []}
          onColorChange={(color) => onCellColorChange(exp.id, 'yard', color)}
          onValueChange={(value) => {
            if (onInlineDateSave) {
              onInlineDateSave(exp.id, 'yard_id', value);
            }
          }}
          fieldLabel="Yard"
          placeholder="Select yard..."
          valueKey="id"
          labelKey="name"
        >
          <ColoredText 
            color={exp.cell_colors?.yard}
            className="text-xs px-2 py-1 rounded cursor-pointer hover:bg-slate-700/50 min-w-[60px] inline-block"
            title="Click to select yard"
          >
            {currentYard?.name || exp.yard_name || '-'}
          </ColoredText>
        </CellSelectColorPicker>
      );
    }
    
    case 'docs':
      return (
        <span 
          className="text-xs text-slate-400 cursor-pointer hover:text-white"
          title={`${exp.documents?.length || 0} document(s)`}
        >
          {exp.documents?.length || 0}
        </span>
      );
    
    default:
      return <span className="text-slate-500">-</span>;
  }
};

export default ExportTableCell;
